import {
  CheckCircle,
  ChevronRight,
  ErrorOutlined,
  Warning,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { getColor } from '../../assets/theme';
import {
  CustomNotificationContent,
  Error500,
  sanitizeMessage,
} from '../../context/app-context';

/**
 * DialogMessage Component
 *
 * This component displays an error dialog with detailed information about the error.
 * It shows an error icon, error message, error details, and provides options to close the dialog or visit the support page.
 *
 * @component
 * @param {Object} props - The component props
 * @param {boolean} props.isOpen - Determines if the dialog is open or closed
 * @param {Error500 | null | undefined} props.error - The error object containing details about the error
 * @param {() => void} props.onClose - Function to be called when closing the dialog
 *
 * @returns {JSX.Element} The rendered DialogMessage component
 */
const DialogMessage = ({
  isOpen,
  error,
  onClose,
  message,
}: {
  isOpen: boolean;
  error: Error500 | null | undefined;
  onClose: () => void;
  message?: string | { title: string; desc: string; status?: string };
}) => {
  const { danger, white } = getColor();

  return (
    <Dialog
      open={isOpen}
      sx={{ minWidth: '300px', '.MuiPaper-root': { overflowY: 'unset' } }}
      maxWidth='xl'
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-36px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 999,
        }}
      >
        <Stack
          sx={{
            width: '79px',
            height: '79px',
            bgcolor: '#FFFF',
            borderRadius: '100%',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {message ? (
            typeof message === 'object' && message.status === 'warning' ? (
              <Warning
                sx={{
                  width: '59px',
                  height: '59px',
                  bgcolor: '#FFF8E1',
                  p: '12px',
                  borderRadius: '100%',
                  color: 'warning.main',
                }}
              />
            ) : (
              <CheckCircle
                sx={{
                  width: '59px',
                  height: '59px',
                  bgcolor: '#e5ffde',
                  p: '12px',
                  borderRadius: '100%',
                  color: 'success.main',
                }}
              />
            )
          ) : (
            <ErrorOutlined
              sx={{
                width: '59px',
                height: '59px',
                bgcolor: '#FFF8F7',
                p: '12px',
                borderRadius: '100%',
                color: '#FF5630',
              }}
            />
          )}
        </Stack>
      </Box>
      <Stack
        sx={{
          bgcolor: white,
          borderRadius: '4px',
          p: '24px 24px 16px 24px',
          justifyContent: 'flex-start',
          width: '460px',
        }}
      >
        <DialogTitle
          variant='title'
          sx={{
            p: '16px 24px',
            fontWeight: 600,
            color:
              typeof message === 'object'
                ? message.status === 'warning'
                  ? 'warning.main'
                  : '#079e3f'
                : 'error.main',
            fontSize: '20px',
            textAlign: 'center',
            wordWrap: 'break-word',
          }}
        >
          {error?.data?.error_message || ''}
          {typeof message === 'object' && message.title}
        </DialogTitle>
        <DialogContent sx={{ p: '0 0 16px 0!important', maxWidth: '405px' }}>
          {!message ? (
            <>
              <Stack sx={{ pb: 2, gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: '16px',
                    wordWrap: 'break-word',
                  }}
                >
                  An error occurred while processing this request :
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: danger,
                    wordWrap: 'break-word',
                  }}
                >
                  {typeof error?.data?.errors?.[0]?.error?.remarks === 'object'
                    ? error?.data?.errors?.[0]?.error?.remarks?.message
                    : error?.data?.errors?.[0]?.error?.remarks}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    color: danger,
                    wordWrap: 'break-word',
                  }}
                >
                  {error?.data?.method} {error?.data?.url || ''}
                </Typography>
              </Stack>
              <Typography
                sx={{
                  pb: 2,
                  fontSize: '14px',
                  color: '#637381',
                  wordWrap: 'break-word',
                }}
              >
                If the error persists, submit a support ticket with a screenshot
                of your concern
              </Typography>
            </>
          ) : (
            message && (
              <Typography
                sx={{
                  fontSize: '16px',
                  wordWrap: 'break-word',
                }}
              >
                <CustomNotificationContent
                  message={sanitizeMessage(
                    typeof message === 'string' ? message : message.desc,
                  )}
                />
              </Typography>
            )
          )}
        </DialogContent>
        <DialogActions sx={{ p: '0 0 8px 0' }}>
          <Stack direction='row' gap='8px'>
            <Button onClick={onClose}>Close</Button>
            {!message && (
              <Link
                to='https://servoitsolutions.ph/support'
                target='_blank'
                style={{ textDecoration: 'none' }}
              >
                <Button endIcon={<ChevronRight />} variant='contained'>
                  Servo IT Solutions - Support
                </Button>
              </Link>
            )}
          </Stack>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};

export default DialogMessage;
